/**
 * Coarse, privacy-preserving client identity for the usage dashboard:
 * a random per-browser UUID (no fingerprinting) plus best-effort
 * platform/model from UA Client Hints. Never anything user-identifying.
 */

const DEVICE_KEY = "barqscan:device";

export function getDeviceId(storage: Storage | undefined): string | undefined {
  if (!storage || typeof crypto === "undefined" || !crypto.randomUUID) {
    return undefined;
  }
  try {
    let id = storage.getItem(DEVICE_KEY);
    if (!id) {
      id = crypto.randomUUID();
      storage.setItem(DEVICE_KEY, id);
    }
    return id;
  } catch {
    return undefined;
  }
}

interface UserAgentDataLike {
  platform?: string;
  getHighEntropyValues?: (hints: string[]) => Promise<{ model?: string }>;
}

export async function detectClient(): Promise<{ platform?: string; model?: string }> {
  if (typeof navigator === "undefined") return {};
  const uad = (navigator as Navigator & { userAgentData?: UserAgentDataLike }).userAgentData;
  if (uad?.getHighEntropyValues) {
    try {
      const high = await uad.getHighEntropyValues(["model"]);
      return { platform: uad.platform, model: high.model || undefined };
    } catch {
      // fall through to UA sniffing
    }
  }
  const ua = navigator.userAgent ?? "";
  const platform = /iPhone|iPad|iPod/.test(ua)
    ? "iOS"
    : /Android/.test(ua)
      ? "Android"
      : /Mac OS X/.test(ua)
        ? "macOS"
        : /Windows/.test(ua)
          ? "Windows"
          : /Linux/.test(ua)
            ? "Linux"
            : undefined;
  return { platform };
}
